import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Eye, EyeOff } from 'lucide-react';
import NECSLogo from './NECSLogo';
import SmsConsentCheckbox, { phoneRequiresConsent } from './SmsConsentCheckbox';
import { toast } from 'sonner';
import { DFW_REGIONS, getRegionByCode } from '../utils/dfwRegions';
import '../styles/public-page-titles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

// Position lists are derived from the badge type — admin badges (NECS-1xxxxx) get
// administrative roles, staff badges (NECS-2xxxxx) get field roles.
const POSITIONS_BY_KIND = {
  necs_admin: [
    'CEO',
    'Director',
    'Manager',
    'Supervisor',
    'Assistant Director',
    'Admin Agent',
  ],
  necs_staff: [
    'Regional Manager',
    'Regional Supervisor',
    'Crew Leader',
    'Site Agent',
  ],
};

// Corporate departments available on the NECS Admin access request.
const ADMIN_DEPARTMENTS = [
  'Executive / C-Suite',
  'Operations',
  'Finance & Accounting',
  'Human Resources',
  'Information Technology',
  'Legal & Compliance',
  'Sales & Business Development',
  'Marketing & Communications',
  'Customer Service',
  'Procurement & Supply Chain',
  'Safety & Risk Management',
  'Training & Development',
];

// Field roles available when registering as NECS Staff (drives badge prefix V|C)
const STAFF_ROLE_TYPES = ['Valet', 'Cleaner'];

const TITLE_BY_KIND = {
  necs_staff: 'NECS Staff Access Request',
  necs_admin: 'NECS Admin Access Request',
};

const SUBCOPY_BY_KIND = {
  necs_staff:
    'Submit your details below. A Noble Empire administrator will review and email you an invite to activate your staff account.',
  necs_admin:
    'Submit your details below. A Noble Empire founder will review and email you an invite to activate your admin account.',
};

/**
 * Company-side access request form, used for "NECS Staff" and "NECS Admin"
 * registration paths. Submissions are saved as access leads — the actual
 * account is created by an existing admin from inside the dashboard.
 */
export default function NECSAccessRequestForm({ kind }) {
  const navigate = useNavigate();
  const positions = POSITIONS_BY_KIND[kind] || POSITIONS_BY_KIND.necs_staff;
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    badge_number: '',
    position: positions[0],
    region: '',         // 3-letter region code (R01-R15) for staff
    city: '',           // selected city within the chosen DFW region
    department: kind === 'necs_admin' ? ADMIN_DEPARTMENTS[0] : '',
    state: 'TX',
    role_type: kind === 'necs_staff' ? STAFF_ROLE_TYPES[0] : '',
  });
  const [loading, setLoading] = useState(false);
  const [badgeLoading, setBadgeLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  // A2P 10DLC — mandatory SMS opt-in whenever a phone number is provided.
  const [smsConsent, setSmsConsent] = useState(false);
  const fetchedKindRef = useRef(null);

  // Build the structured-badge query string from the current form context.
  // Admin -> kind+department+position+state ; Staff -> kind+role+position+region+state.
  const buildBadgeQuery = (f) => {
    const qs = new URLSearchParams({ kind });
    if (kind === 'necs_admin') {
      if (f.department) qs.set('department', f.department);
      if (f.position) qs.set('position', f.position);
      if (f.state) qs.set('state', f.state);
    } else {
      if (f.role_type) qs.set('role', f.role_type);
      if (f.position) qs.set('position', f.position);
      if (f.region) qs.set('region', f.region);
      if (f.state) qs.set('state', f.state);
    }
    return qs.toString();
  };

  const refreshBadge = async (snapshot) => {
    setBadgeLoading(true);
    try {
      const url = `${BACKEND_URL}/api/access-requests/generate-badge?${buildBadgeQuery(snapshot)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data?.badge_number) {
        setForm((s) => ({ ...s, badge_number: data.badge_number }));
      }
    } catch (_) {
      /* silent — user can still submit and admin will assign */
    } finally {
      setBadgeLoading(false);
    }
  };

  // Auto-generate badge on mount, scoped to the kind + current default context.
  // Guarded by a ref so StrictMode's double-mount only triggers one fetch.
  useEffect(() => {
    if (fetchedKindRef.current === kind) return;
    fetchedKindRef.current = kind;
    // Pass the initial form context so first-fetch is already structured.
    refreshBadge({
      department: kind === 'necs_admin' ? ADMIN_DEPARTMENTS[0] : '',
      position: positions[0],
      region: '',
      state: 'TX',
      role_type: kind === 'necs_staff' ? STAFF_ROLE_TYPES[0] : '',
    });
   
  }, [kind]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match. Please confirm your password.');
      return;
    }
    if (phoneRequiresConsent(form.phone, smsConsent)) {
      toast.error('Please agree to receive SMS messages before continuing, or clear the phone number.');
      return;
    }
    setLoading(true);
    try {
      const { confirm_password, ...payload } = form;
      const res = await fetch(`${BACKEND_URL}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, kind }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not submit.');
      toast.success(data?.message || 'Thanks — admin will review your request.');
      setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl p-8" data-testid={`access-request-form-${kind}`}>
      <div className="flex justify-center mb-4">
        <NECSLogo variant="header" size="sm" dataTestId={`access-request-logo-${kind}`} />
      </div>

      <h2
        className="necs-web-page-title text-center mb-2"
        data-testid="access-request-title"
      >
        {TITLE_BY_KIND[kind]}
      </h2>
      <p className="text-center text-sm text-zinc-600 mb-6">{SUBCOPY_BY_KIND[kind]}</p>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">First Name *</Label>
            <Input id="first_name" required value={form.first_name} onChange={set('first_name')} data-testid="acc-first-name" />
          </div>
          <div>
            <Label htmlFor="last_name">Last Name *</Label>
            <Input id="last_name" required value={form.last_name} onChange={set('last_name')} data-testid="acc-last-name" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" required value={form.email} onChange={set('email')} data-testid="acc-email" />
          </div>
          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input id="phone" type="tel" required value={form.phone} onChange={set('phone')} placeholder="+1 214 555 0123" data-testid="acc-phone" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="acc_password">Password *</Label>
            <div className="relative">
              <Input
                id="acc_password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength={6}
                value={form.password}
                onChange={set('password')}
                className="pr-10"
                data-testid="acc-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-zinc-500 hover:text-[#EAB308]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="acc-password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              Save this password — it will be activated once your access request is approved.
            </p>
          </div>
          <div>
            <Label htmlFor="acc_confirm_password">Confirm Password *</Label>
            <Input
              id="acc_confirm_password"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={6}
              value={form.confirm_password}
              onChange={set('confirm_password')}
              data-testid="acc-confirm-password"
            />
            {form.confirm_password && form.password !== form.confirm_password && (
              <p className="text-xs text-red-500 mt-1" data-testid="acc-confirm-password-mismatch">
                Passwords do not match.
              </p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="badge_number">Badge Number (auto-assigned)</Label>
            <div className="flex gap-2">
              <Input
                id="badge_number"
                value={badgeLoading ? 'Generating…' : form.badge_number}
                readOnly
                className="bg-zinc-100 cursor-not-allowed font-mono flex-1"
                data-testid="acc-badge"
              />
              <Button
                type="button"
                onClick={() => refreshBadge(form)}
                disabled={badgeLoading}
                className="bg-zinc-800 text-white hover:bg-zinc-700 font-bold px-3"
                data-testid="acc-badge-refresh"
                title="Regenerate badge using the current Department/Role/Position/Region/State"
              >
                ↻
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              {kind === 'necs_admin'
                ? 'Format: NECS-A-[DEPT]-[POSITION]-[STATE]-[SEQ]'
                : 'Format: NECS-S-[ROLE]-[POSITION]-[REGION]-[STATE]-[SEQ]'}
            </p>
          </div>
          <div>
            <Label htmlFor="position">Position *</Label>
            <select
              id="position"
              required
              value={form.position}
              onChange={set('position')}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
              data-testid="acc-position"
            >
              {positions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">
              Options are scoped to your account type ({kind === 'necs_admin' ? 'Administration' : 'Regular Agent'}).
            </p>
          </div>
        </div>

        {/* Field-role selector — only on staff registration (drives badge V|C prefix) */}
        {kind === 'necs_staff' && (
          <div>
            <Label htmlFor="role_type">Field Role *</Label>
            <select
              id="role_type"
              required
              value={form.role_type}
              onChange={set('role_type')}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
              data-testid="acc-role-type"
            >
              {STAFF_ROLE_TYPES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="text-[11px] text-zinc-500 mt-1">
              Valet handles trash collection; Cleaner handles community cleaning &amp; pressure-washing.
            </p>
          </div>
        )}

        {kind === 'necs_admin' ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="department">Department *</Label>
              <select
                id="department"
                required
                value={form.department}
                onChange={set('department')}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                data-testid="acc-department"
              >
                {ADMIN_DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                Corporate department this admin role will support.
              </p>
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <select
                id="state"
                required
                value={form.state}
                onChange={set('state')}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                data-testid="acc-state"
              >
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ) : (
          /* DFW Metroplex Region → City → State cascade (staff registration only). */
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="region">Region *</Label>
              <select
                id="region"
                required
                value={form.region}
                onChange={(e) => {
                  const nextCode = e.target.value;
                  // Reset the city whenever the region changes so we never carry
                  // a stale city from a different region into the badge/profile.
                  setForm((s) => ({ ...s, region: nextCode, city: '' }));
                }}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                data-testid="acc-region"
              >
                <option value="">— Select a region —</option>
                {DFW_REGIONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code} · {r.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                DFW Metroplex business region. Drives the [REGION] segment of your badge.
              </p>
            </div>
            <div>
              <Label htmlFor="city">City *</Label>
              <select
                id="city"
                required
                value={form.city}
                onChange={set('city')}
                disabled={!form.region}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A] disabled:bg-zinc-100 disabled:text-zinc-400"
                data-testid="acc-city"
              >
                <option value="">{form.region ? '— Select a city —' : 'Pick a region first'}</option>
                {(getRegionByCode(form.region)?.cities || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <select
                id="state"
                required
                value={form.state}
                onChange={set('state')}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 bg-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                data-testid="acc-state"
              >
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}

        <SmsConsentCheckbox
          checked={smsConsent}
          onChange={setSmsConsent}
          testid="acc-sms-consent"
        />

        <Button
          type="submit"
          className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
          disabled={loading || phoneRequiresConsent(form.phone, smsConsent)}
          data-testid="acc-submit"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </form>

      <p className="text-center text-sm text-zinc-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="acc-login-link">
          Login here
        </Link>
      </p>

      <p className="text-center text-sm text-zinc-600 mt-3">
        <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="acc-back-prev-link">
          ← Back to Previous Page
        </Link>
      </p>
    </Card>
  );
}
